// pages/TiposCancelacion.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  Divider,
  Alert,
  InputNumber,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  TagOutlined,
  WarningOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  BgColorsOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;

// Mapeo de colores enteros a colores CSS
const COLOR_MAP = {
  0: '#d9d9d9',
  1: '#1890ff',
  2: '#52c41a',
  3: '#faad14',
  4: '#ff4d4f',
  5: '#722ed1',
  6: '#13c2c2',
  7: '#eb2f96',
  8: '#fa8c16',
  9: '#2f54eb',
  10: '#a0d911',
  11: '#f5222d',
  12: '#fa541c',
  13: '#d4b106',
  14: '#096dd9',
  15: '#237804',
};

const getColorFromInt = (colorInt) => {
  return COLOR_MAP[colorInt] || '#d9d9d9';
};

const getColorName = (colorInt) => {
  const names = {
    0: 'Sin color',
    1: 'Azul',
    2: 'Verde',
    3: 'Amarillo',
    4: 'Rojo',
    5: 'Morado',
    6: 'Cian',
    7: 'Rosa',
    8: 'Naranja',
    9: 'Azul oscuro',
    10: 'Lima',
    11: 'Rojo intenso',
    12: 'Naranja intenso',
    13: 'Dorado',
    14: 'Azul profundo',
    15: 'Verde oscuro'
  };
  return names[colorInt] || `Color ${colorInt}`;
};

const TiposCancelacion = () => {
  // Estados
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentTipo, setCurrentTipo] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    con_factura: 0,
    sin_factura: 0,
    con_cargo: 0,
    sin_cargo: 0
  });

  // Estados para cargos
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);

  // Opciones de colores para el select
  const colorOptions = [
    { value: 0, label: 'Sin color', color: '#d9d9d9' },
    { value: 1, label: 'Azul', color: '#1890ff' },
    { value: 2, label: 'Verde', color: '#52c41a' },
    { value: 3, label: 'Amarillo', color: '#faad14' },
    { value: 4, label: 'Rojo', color: '#ff4d4f' },
    { value: 5, label: 'Morado', color: '#722ed1' },
    { value: 6, label: 'Cian', color: '#13c2c2' },
    { value: 7, label: 'Rosa', color: '#eb2f96' },
    { value: 8, label: 'Naranja', color: '#fa8c16' },
    { value: 9, label: 'Azul oscuro', color: '#2f54eb' },
    { value: 10, label: 'Lima', color: '#a0d911' },
    { value: 11, label: 'Rojo intenso', color: '#f5222d' },
    { value: 12, label: 'Naranja intenso', color: '#fa541c' },
    { value: 13, label: 'Dorado', color: '#d4b106' },
    { value: 14, label: 'Azul profundo', color: '#096dd9' },
    { value: 15, label: 'Verde oscuro', color: '#237804' },
  ];

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 120,
      sorter: (a, b) => a.Codigo?.localeCompare(b.Codigo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo, record) => {
        const color = getColorFromInt(record.Color || 0);
        return (
          <Tag 
            color={color} 
            style={{ 
              fontWeight: 'bold', 
              fontSize: '14px',
              padding: '4px 8px',
              border: `2px solid ${color}`
            }}
          >
            {codigo}
          </Tag>
        );
      },
      fixed: 'left',
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 250,
      sorter: (a, b) => a.Descripcion?.localeCompare(b.Descripcion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      render: (descripcion) => (
        <div style={{ maxWidth: 250 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {descripcion}
          </Text>
        </div>
      ),
    },
    {
      title: 'Código Cargo',
      dataIndex: 'CodigoCargo',
      key: 'CodigoCargo',
      width: 200,
      sorter: (a, b) => a.CodigoCargo?.localeCompare(b.CodigoCargo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'CodigoCargo' && sortedInfo.order,
      render: (codigoCargo) => {
        const cargo = cargos.find(c => c.codigo === codigoCargo);
        return codigoCargo ? (
          <Tooltip title={cargo ? cargo.descripcion : 'Cargo no encontrado'}>
            <Tag color="green" style={{ fontFamily: 'monospace', fontSize: '13px', cursor: 'help' }}>
              <ShoppingOutlined style={{ marginRight: 4 }} />
              {codigoCargo}
              {cargo && (
                <span style={{ marginLeft: 6, fontWeight: 'normal', fontSize: '11px', color: '#666' }}>
                  - {cargo.descripcion}
                </span>
              )}
            </Tag>
          </Tooltip>
        ) : (
          <Tag color="default" style={{ fontFamily: 'monospace' }}>
            Sin cargo
          </Tag>
        );
      },
    },
    {
      title: 'Factura',
      dataIndex: 'SeFactura',
      key: 'SeFactura',
      width: 120,
      sorter: (a, b) => (a.SeFactura ? 1 : 0) - (b.SeFactura ? 1 : 0),
      sortOrder: sortedInfo.columnKey === 'SeFactura' && sortedInfo.order,
      render: (seFactura) => (
        seFactura ? (
          <Badge
            status="success"
            text="Factura"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No factura"
            style={{ color: '#8c8c8c', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Factura', value: true },
        { text: 'No factura', value: false },
      ],
      filteredValue: filteredInfo.SeFactura || null,
      onFilter: (value, record) => record.SeFactura === value,
    },
    {
      title: 'Color',
      dataIndex: 'Color',
      key: 'Color',
      width: 150,
      sorter: (a, b) => (a.Color || 0) - (b.Color || 0),
      sortOrder: sortedInfo.columnKey === 'Color' && sortedInfo.order,
      render: (color) => {
        const colorCss = getColorFromInt(color || 0);
        const colorName = getColorName(color || 0);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 4,
                backgroundColor: colorCss,
                border: '1px solid #d9d9d9'
              }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {colorName} ({color || 0})
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar tipo de cancelación?"
            description="Esta acción eliminará el tipo de cancelación permanentemente"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchTipos();
    fetchEstadisticas();
    fetchCargos();
  }, []);

  const fetchTipos = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      const response = await axios.get(`${BASE_URL}/api/tipos-cancelacion`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Descripcion: item.Descripcion || '',
        CodigoCargo: item.CodigoCargo || '',
        SeFactura: Boolean(item.SeFactura),
        Color: item.Color || 0
      }));
      
      setTipos(cleanedData);
      
    } catch (error) {
      console.error('❌ Error cargando tipos de cancelación:', error);
      message.error('Error al cargar los tipos de cancelación');
      setTipos([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tipos-cancelacion/estadisticas/resumen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEstadisticas(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  // Cargar cargos para el select
  const fetchCargos = async () => {
    setLoadingCargos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/cargos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { soloActivos: false }
      });
      

    // console.log(response.data.data);
    

      setCargos(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando cargos:', error);
      setCargos([]);
    } finally {
      setLoadingCargos(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    const params = {
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    fetchTipos(params);
  };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentTipo(null);
    form.resetFields();
    
    form.setFieldsValue({
      CodigoCargo: null,
      SeFactura: false,
      Color: 0
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentTipo(record);
    
    const formValues = {
      Codigo: record.Codigo,
      Descripcion: record.Descripcion || '',
      CodigoCargo: record.CodigoCargo || null,
      SeFactura: record.SeFactura || false,
      Color: record.Color || 0
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    const colorCss = getColorFromInt(record.Color || 0);
    const colorName = getColorName(record.Color || 0);
    const cargo = cargos.find(c => c.codigo === record.CodigoCargo);
    
    Modal.info({
      title: `Tipo de Cancelación ${record.Codigo}`,
      width: 600,
      icon: <TagOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag 
                  color={colorCss} 
                  style={{ 
                    fontSize: '18px', 
                    padding: '6px 12px',
                    border: `2px solid ${colorCss}`
                  }}
                >
                  <BarcodeOutlined style={{ marginRight: 8 }} />
                  {record.Codigo}
                </Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ 
                marginTop: 4, 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {record.Descripcion || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Código Cargo:</strong>
              <div style={{ marginTop: 4 }}>
                {record.CodigoCargo ? (
                  <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    <ShoppingOutlined style={{ marginRight: 4 }} />
                    {record.CodigoCargo}
                    {cargo && (
                      <span style={{ marginLeft: 6, fontWeight: 'normal', fontSize: '12px', color: '#666' }}>
                        - {cargo.descripcion}
                      </span>
                    )}
                  </Tag>
                ) : (
                  <Tag color="default" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    Sin cargo asignado
                  </Tag>
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Factura:</strong>
              <div style={{ marginTop: 4 }}>
                {record.SeFactura ? (
                  <Badge 
                    status="success" 
                    text="Factura" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No factura" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Color:</strong>
              <div style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: colorCss,
                      border: '2px solid #d9d9d9'
                    }}
                  />
                  <div>
                    <Text style={{ fontFamily: 'monospace', display: 'block' }}>
                      Valor: {record.Color || 0}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {colorName}
                    </Text>
                  </div>
                </div>
              </div>
            </Col>
            
            <Col span={24}>
              <Divider />
              <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>💡 Información:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>Factura:</strong> Indica si este tipo de cancelación genera facturación</li>
                    <li><strong>Código Cargo:</strong> Cargo asociado que se aplicará al facturar</li>
                    <li><strong>Color:</strong> Código numérico identificativo para visualización en el sistema</li>
                  </ul>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/tipos-cancelacion/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTipos(tipos.filter(t => t.Codigo !== codigo));
      
      message.success('Tipo de cancelación eliminado correctamente');
      fetchEstadisticas();
      
    } catch (error) {
      console.error('❌ Error eliminando tipo de cancelación:', error);
      
      if (error.response?.data?.error) {
        Modal.error({
          title: 'No se puede eliminar',
          content: (
            <div>
              <p>{error.response.data.error}</p>
              {error.response.data.details && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {error.response.data.details.reservas && 
                      `Está siendo utilizado en ${error.response.data.details.reservas} reservas`
                    }
                  </Text>
                </div>
              )}
            </div>
          )
        });
      } else {
        message.error(error.response?.data?.error || 'Error al eliminar el tipo de cancelación');
      }
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      // Si CodigoCargo está vacío o es null, guardar como ''
      values.CodigoCargo = values.CodigoCargo || '';

      // Asegurar que SeFactura sea booleano
      values.SeFactura = Boolean(values.SeFactura);

      // Asegurar que Color sea un entero
      values.Color = parseInt(values.Color) || 0;

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/tipos-cancelacion`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchTipos();
        fetchEstadisticas();
        message.success('Tipo de cancelación creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/tipos-cancelacion/${currentTipo.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const tipoActualizado = {
          ...values
        };
        
        setTipos(prevTipos => 
          prevTipos.map(tipo => 
            tipo.Codigo === currentTipo.Codigo
              ? tipoActualizado 
              : tipo
          )
        );
        
        fetchEstadisticas();
        message.success('Tipo de cancelación actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando tipo de cancelación:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchTipos({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchTipos({ current: 1 });
  };

  const handleExport = () => {
    if (tipos.length === 0) {
      message.warning('No hay datos para exportar');
      return;
    }

    const headers = ['Código', 'Descripción', 'Código Cargo', 'Factura', 'Color'];
    const rows = tipos.map(item => [
      item.Codigo,
      item.Descripcion,
      item.CodigoCargo || 'Sin cargo',
      item.SeFactura ? 'Sí' : 'No',
      item.Color || 0
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tipos_cancelacion_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    message.success('CSV exportado correctamente');
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space wrap>
        <Input
          placeholder="Buscar por código, descripción, código cargo..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
      </Space>
    </div>
  );

  // Estadísticas
  const estadisticasCards = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={6}>
        <Card>
          <Statistic 
            title="Total Tipos" 
            value={estadisticas.total || 0}
            prefix={<TagOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={6}>
        <Card>
          <Statistic 
            title="Factura" 
            value={estadisticas.con_factura || 0}
            prefix={<CheckSquareOutlined />}
            valueStyle={{ color: '#52c41a' }}
            suffix={`/ ${estadisticas.total || 0}`}
          />
        </Card>
      </Col>
      <Col xs={24} sm={6}>
        <Card>
          <Statistic 
            title="No Factura" 
            value={estadisticas.sin_factura || 0}
            prefix={<CloseSquareOutlined />}
            valueStyle={{ color: '#8c8c8c' }}
            suffix={`/ ${estadisticas.total || 0}`}
          />
        </Card>
      </Col>
      <Col xs={24} sm={6}>
        <Card>
          <Statistic 
            title="Con Cargo" 
            value={estadisticas.con_cargo || 0}
            prefix={<ShoppingOutlined />}
            valueStyle={{ color: '#fa8c16' }}
            suffix={`/ ${estadisticas.total || 0}`}
          />
        </Card>
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Tipos de Cancelación"
        subtitle={`${tipos.length} tipos registrados`}
        icon={<TagOutlined />}
        loading={loading}
        onRefresh={() => fetchTipos()}
        onCreate={handleCreate}
        badgeCount={tipos.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticasCards}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={tipos}
          rowKey={(record) => record.Codigo}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1100, y: 500 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron tipos de cancelación'
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} tipos`,
            pageSizeOptions: ['10', '20', '50', '100'],
            defaultPageSize: 20
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar tipo de cancelación */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Tipo de Cancelación' : 'Editar Tipo de Cancelación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            CodigoCargo: null,
            SeFactura: false,
            Color: 0
          }}
        >
          {/* Primera fila: Código y Descripción */}
          <Row gutter={16}>
            <Col span={8}>
                  <Form.Item
                    name="Codigo"
                    label="Código"
                    rules={[
                        { 
                        required: true, 
                        message: 'Por favor ingrese el código'
                        },
                        {
                        pattern: /^[0-9]+$/,
                        message: 'El código solo puede contener números'
                        },
                        {
                        max: 3,
                        message: 'El código no puede tener más de 3 dígitos'
                        }
                    ]}
                    tooltip="Código numérico único del tipo de cancelación (máx. 3 dígitos)"
                    >
                    <Input 
                        placeholder="Ej: 1, 2, 3"
                        maxLength={3}
                        disabled={modalType === 'edit'}
                        prefix={<BarcodeOutlined />}
                        onChange={(e) => {
                        // Solo permitir números
                        const value = e.target.value.replace(/\D/g, '');
                        e.target.value = value;
                        form.setFieldsValue({ Codigo: value });
                        }}
                    />
                </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese la descripción',
                    max: 100
                  }
                ]}
                tooltip="Descripción completa del tipo de cancelación"
              >
                <Input 
                  placeholder="Descripción del tipo de cancelación"
                  maxLength={100}
                  showCount
                  prefix={<FileTextOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: Código Cargo (select) y Factura */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="CodigoCargo"
                label="Código Cargo"
                tooltip="Selecciona el cargo asociado para facturar (opcional)"
                extra={
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Si no se selecciona, el tipo de cancelación no tendrá cargo asignado
                  </Text>
                }
              >
                <Select
                  placeholder="Seleccione un cargo"
                  allowClear
                  showSearch
                  loading={loadingCargos}
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                        // Buscar en el label (que incluye código y descripción)
                        const label = option?.label?.toLowerCase() || '';
                        console.log(label);
                        
                        return label.includes(input.toLowerCase());
                  }}
                  notFoundContent={loadingCargos ? <Spin size="small" /> : "No hay cargos disponibles"}
                  style={{ width: '100%' }}
                  suffixIcon={<ShoppingOutlined />}
                >
                  {cargos.map(cargo => (
                    <Option key={cargo.codigo} value={cargo.codigo} label={`${cargo.codigo} - ${cargo.descripcion}`}>
                      <Space>
                        <Tag color="blue">{cargo.codigo}</Tag>
                        <span>{cargo.descripcion}</span>
                        <Tag color="green" style={{ fontSize: '11px' }}>
                          {cargo.precio?.toFixed(2)} €
                        </Tag>
                        {!cargo.activo && (
                          <Tag color="red" style={{ fontSize: '11px' }}>Inactivo</Tag>
                        )}
                        <Tag color="purple" style={{ fontSize: '11px' }}>
                          {cargo.seccion}
                        </Tag>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="SeFactura"
                label="Factura"
                valuePropName="checked"
                tooltip="Indica si este tipo de cancelación genera facturación"
              >
                <Switch
                  checkedChildren="Factura"
                  unCheckedChildren="No factura"
                  style={{ minWidth: 100 }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tercera fila: Color */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Color"
                label="Color"
                tooltip="Código numérico del color identificativo (0 = sin color)"
                rules={[
                  {
                    validator: (_, value) => {
                      const num = parseInt(value);
                      if (isNaN(num) || num < 0 || num > 99) {
                        return Promise.reject('El color debe ser un número entre 0 y 99');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <div>
                  <Row gutter={8}>
                    <Col span={8}>
                      <InputNumber
                        min={0}
                        max={99}
                        style={{ width: '100%' }}
                        placeholder="0 = sin color"
                        onChange={(value) => {
                          form.setFieldsValue({ Color: value || 0 });
                        }}
                      />
                    </Col>
                    <Col span={16}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {colorOptions.map((opt) => (
                          <div
                            key={opt.value}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 4,
                              backgroundColor: opt.color,
                              cursor: 'pointer',
                              border: form.getFieldValue('Color') === opt.value 
                                ? '3px solid #1890ff' 
                                : '2px solid #d9d9d9',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}
                            onClick={() => {
                              form.setFieldsValue({ Color: opt.value });
                            }}
                            title={opt.label}
                          >
                            {opt.value === 0 && (
                              <span style={{ color: '#999', fontSize: '12px' }}>✕</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      <BgColorsOutlined /> Haz clic en un color para seleccionarlo o ingresa el número manualmente
                    </Text>
                    {form.getFieldValue('Color') !== undefined && form.getFieldValue('Color') !== null && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Color seleccionado: {getColorName(form.getFieldValue('Color'))} ({form.getFieldValue('Color')})
                        </Text>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 4,
                            backgroundColor: getColorFromInt(form.getFieldValue('Color') || 0),
                            border: '1px solid #d9d9d9',
                            display: 'inline-block',
                            marginLeft: 8,
                            verticalAlign: 'middle'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Código:</strong> Identificador único del tipo de cancelación</li>
                  <li><strong>Descripción:</strong> Nombre descriptivo del tipo de cancelación</li>
                  <li><strong>Código Cargo:</strong> Selecciona el cargo que se aplicará al facturar (opcional)</li>
                  <li><strong>Factura:</strong> Activar si este tipo genera facturación</li>
                  <li><strong>Color:</strong> Código numérico (0-99) para identificación visual</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <strong>💡 Ejemplos comunes:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>NO_SHOW</strong> - No presentación (con cargo, factura: Sí, color: 1)</li>
                    <li><strong>CANCEL</strong> - Cancelación anticipada (con cargo, factura: Sí, color: 4)</li>
                    <li><strong>MODIFY</strong> - Modificación (sin cargo, factura: No, color: 3)</li>
                    <li><strong>LATE</strong> - Cancelación tardía (con cargo, factura: Sí, color: 11)</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}

          {modalType === 'edit' && (
            <Alert
              message="Modificación de tipo de cancelación"
              description={
                <div>
                  <p>Los cambios en el tipo de cancelación afectarán a las reservas que lo utilicen.</p>
                  {currentTipo?.CodigoCargo && (
                    <p style={{ marginTop: 4 }}>
                      <Tag color="green">
                        <ShoppingOutlined style={{ marginRight: 4 }} />
                        Cargo actual: {currentTipo.CodigoCargo}
                        {cargos.find(c => c.codigo === currentTipo.CodigoCargo)?.descripcion && (
                          <span style={{ marginLeft: 6, fontWeight: 'normal' }}>
                            - {cargos.find(c => c.codigo === currentTipo.CodigoCargo)?.descripcion}
                          </span>
                        )}
                      </Tag>
                    </p>
                  )}
                  {!currentTipo?.CodigoCargo && (
                    <p style={{ marginTop: 4 }}>
                      <Tag color="default">Sin cargo asignado actualmente</Tag>
                    </p>
                  )}
                  <p style={{ marginTop: 4 }}>
                    <Tag color={currentTipo?.SeFactura ? 'green' : 'default'}>
                      {currentTipo?.SeFactura ? 'Factura: Sí' : 'Factura: No'}
                    </Tag>
                  </p>
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Color actual:</span>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        backgroundColor: getColorFromInt(currentTipo?.Color || 0),
                        border: '1px solid #d9d9d9'
                      }}
                    />
                    <Text type="secondary">
                      {getColorName(currentTipo?.Color || 0)} ({currentTipo?.Color || 0})
                    </Text>
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

export default TiposCancelacion;